import * as React from 'react';

import { useOptionalCurrentAgencyUser } from './current-user';

/**
 * Statsig gates this app reads. Each value must match an entry in the registry
 * in pkg/core/statsig/frontend_flags.go exactly -- a gate the server never
 * reports is silently always-off, which feature-flags.test.tsx guards against.
 *
 * Adding a flag is one entry here plus one in the Go registry. See the Feature
 * Flags section of CLAUDE.md.
 */
export const FEATURE_FLAGS = {
  HIDE_ACCOUNTS_POLICIES_TABS: 'hide_accounts_policies_tabs',
  CONVERT_WORKSPACE_TO_PRESET: 'convert_workspace_to_preset_enabled',
  SHOW_CLIENTS_TAB: 'show_clients_tab',
  SMART_FILL: 'smart_fill_enabled',
  SMART_FILL_EXTENSION_ONLY: 'smart_fill_extension_only',
  SMART_FILL_ACORD_BUCKETS: 'smart_fill_acord_buckets_enabled',
  MAILBOX: 'mailbox_enabled',
  MAILBOX_SCHEDULED_SEND: 'mailbox_scheduled_send_enabled',
  OUTBOUND_CALL_FREQUENCY_CAP: 'cara_outbound_call_frequency_cap_enabled',
  AMS_RECONCILIATION: 'ams_reconciliation_enabled',
  ANNUAL_RENEWAL_BANNER: 'annual_renewal_banner_enabled',
  SHOW_WORKFLOWS_TAB: 'show_workflows_tab',
  HIDE_MAILBOX_TRIGGER_EVENT: 'hide_mailbox_trigger_event',
  DOCUMENT_GENERATION_V4: 'document_generation_v4_enabled',
  AGENCY_BRAND_DESIGN: 'agency_brand_design_enabled',
  ANALYTICS_AMS_WRITES: 'analytics_ams_writes_enabled',
  ANALYTICS_PRESET_RANKINGS: 'analytics_preset_rankings_enabled',
  ANALYTICS_SEGMENT_FILTERS: 'analytics_segment_filters_enabled',
  ANALYTICS_CSV_EXPORT: 'analytics_csv_export_enabled',
  ANALYTICS_CSV_EXPORT_SUBAGENCIES: 'analytics_csv_export_subagencies_enabled',
  TRANSFER_DIRECTORY_CSV_IMPORT: 'transfer_directory_csv_import_enabled',
  AGENCY_USER_CSV_IMPORT: 'agency_user_csv_import_enabled',
  EFFORT_LEVEL_SELECTOR: 'effort_level_selector_enabled',
  EFFORT_LEVEL_AUTO: 'effort_level_auto_enabled',
  EMAIL_TEMPLATES: 'mailbox_email_templates_enabled',
  MAILBOX_BULK_SEND: 'mailbox_bulk_send_enabled',
  CLIENT_FIELD_PROVENANCE: 'client_field_provenance_enabled',
  MAILBOX_AMS_LOGGING: 'mailbox_ams_logging_enabled',
  CONNECTED_CLIENT_RECORD: 'connected_client_record_enabled'
} as const;

export type FeatureFlag = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

/**
 * The caller's enabled flags. Reach for this only where the flag being tested is
 * not known at the call site, as in the module-level navigation predicates;
 * prefer useFlag everywhere else.
 *
 * Stays ReadonlySet<string> rather than Set<FeatureFlag> because the server
 * legitimately reports names a given build predates. Outside a
 * CurrentUserProvider (admin embedding AIS Conversation) every flag is off.
 */
export const useFeatureFlags = (): ReadonlySet<string> => {
  const agencyUser = useOptionalCurrentAgencyUser();
  return React.useMemo(() => new Set(agencyUser?.frontendFlags ?? []), [agencyUser?.frontendFlags]);
};

/**
 * True when the named gate is on for the current caller.
 *
 * @example
 * const hideLegacyTabs = useFlag(FEATURE_FLAGS.HIDE_ACCOUNTS_POLICIES_TABS);
 */
export const useFlag = (name: FeatureFlag): boolean => useFeatureFlags().has(name);
